/**
 * The card's per-element custom CSS → one stylesheet string, confined to the
 * preview (spec R67, R68).
 *
 * Defence in depth, and both halves live here so neither can be skipped:
 * `parseDeclarations` drops everything that is not a plain declaration, and every
 * surviving rule is prefixed with the one preview-scope class, so even a rule that
 * somehow named a wider selector could only ever match inside the preview card.
 *
 * The result is rendered as the text child of a single `<style>` element by
 * `CardPreview` — never through `innerHTML`, `dangerouslySetInnerHTML` or
 * `insertRule` (ADR-4A).
 */

import { PREVIEW_SCOPE_CLASS } from '../constants';
import { parseDeclarations } from './parseDeclarations';

/** One element's raw contribution: where it applies, and what the user typed. */
export interface CardCssRule {
  /** A selector from `constants/FIELD_SELECTORS` — module data, not user input. */
  selector: string;
  /** The user's text, exactly as stored. */
  css: string;
}

/**
 * Selectors this module is willing to emit: a class or id selector, possibly
 * descended, made of the characters our own table uses.
 *
 * The table is module data, so this can never fire today. It is here because the
 * scope prefix is only a guarantee while what follows it is a selector — a check
 * that costs one regex is cheaper than trusting that no future caller ever passes
 * this function something else.
 */
const SAFE_SELECTOR_PATTERN = /^[.#][-_a-zA-Z0-9]+(?: [.#]?[-_a-zA-Z0-9]+)*$/;

/**
 * One element's rule, scoped to the preview.
 *
 * The element **and its descendants**, not the element alone. In the prototype a
 * field's block is one element with a text node in it, so `color: #D60027` on the
 * block is what the shopper sees. Here a block is a small tree of design-system
 * components, and the text sits on a child carrying its own utility class — a
 * `color` on the wrapper alone would be overridden by `tw-text-ink` on the
 * child, so the user would type a declaration and see nothing happen. Two selectors
 * put the user's declaration above the utility class (two classes beat one) and make
 * R67's "applied live to preview" true for the whole block.
 *
 * Both selectors carry the scope prefix, so this widens what a declaration reaches
 * *inside* the card and changes nothing about R68's containment.
 */
const ruleFor = (selector: string, declarations: string): string =>
  `.${PREVIEW_SCOPE_CLASS} ${selector}, .${PREVIEW_SCOPE_CLASS} ${selector} * { ${declarations} }`;

export const composeCardStylesheet = (rules: CardCssRule[]): string => {
  const emitted: string[] = [];

  rules.forEach((rule) => {
    if (!SAFE_SELECTOR_PATTERN.test(rule.selector)) {
      return;
    }
    const declarations = parseDeclarations(rule.css);
    if (declarations === '') {
      return;
    }
    emitted.push(ruleFor(rule.selector, declarations));
  });

  return emitted.join('\n');
};
